"use client"

import { useEffect, useState } from "react"
import axios from "axios"
import { motion } from "framer-motion"
import { CreditCard, AlertCircle, CheckCircle } from "lucide-react"
import { loadMercadoPago } from "@mercadopago/sdk-js"
import "../styles/checkout-cartao.css"

const CheckoutCartao = ({ pedidoId, total, onSuccess }) => {
  const [statusPagamento, setStatusPagamento] = useState("Aguardando pagamento...")
  const [loading, setLoading] = useState(false)
  const [mp, setMp] = useState(null)
  const [paymentMethodId, setPaymentMethodId] = useState("")
  const [installmentsOptions, setInstallmentsOptions] = useState([])
  const [cardToken, setCardToken] = useState("")
  const [identificationTypes, setIdentificationTypes] = useState([])
  const [erro, setErro] = useState("")
  const [sdkLoading, setSdkLoading] = useState(true)

  useEffect(() => {
    const script = document.createElement("script")
    script.src = "https://sdk.mercadopago.com/js/v2"
    script.async = true
    script.onload = () => {
      console.log("MercadoPago SDK carregado!")
      setSdkLoading(false)
    }
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])
  useEffect(() => {
    // Limpa sessão anterior (ex: Pix ou boleto)
    const cleanup = () => {
      if (window.MercadoPago) {
        try {
          window.MercadoPago.clearSession && window.MercadoPago.clearSession()
        } catch (e) {
          console.warn("Erro ao limpar sessão MercadoPago:", e)
        }
      }
    }
  
    cleanup()
  
    return cleanup
  }, [])
  useEffect(() => {
    const initMercadoPago = async () => {
      if (sdkLoading) return

      try {
        await loadMercadoPago()
        const mercadoPago = new window.MercadoPago("APP_USR-c3f565e2-4418-403d-b7d0-9e23f616ade8", { locale: "pt-BR" })
        setMp(mercadoPago)
        initializeCardForm(mercadoPago)
        buscarTiposDocumento(mercadoPago)
      } catch (error) {
        console.error("Erro ao inicializar MercadoPago:", error)
        setErro("Não foi possível carregar o formulário de pagamento.")
      }
    }

    const initializeCardForm = (mercadoPago) => {
      try {
        const cardNumberElement = mercadoPago.fields.create("cardNumber", { placeholder: "Número do cartão" })
        const expirationDateElement = mercadoPago.fields.create("expirationDate", { placeholder: "MM/YY" })
        const securityCodeElement = mercadoPago.fields.create("securityCode", { placeholder: "Código de segurança" })

        cardNumberElement.mount("form-checkout__cardNumber")
        expirationDateElement.mount("form-checkout__expirationDate")
        securityCodeElement.mount("form-checkout__securityCode")

        cardNumberElement.on("binChange", async (data) => {
          if (data.bin && data.bin.length >= 6) {
            await buscarBandeiraEParcelas(mercadoPago, data.bin)
          }
        })
      } catch (error) {
        console.error("Erro ao inicializar formulário:", error)
        setErro("Erro ao carregar campos do cartão. Tente novamente.")
      }
    }

    if (!sdkLoading) {
      initMercadoPago()
    }
  }, [sdkLoading])

  const buscarTiposDocumento = async (mercadoPago) => {
    try {
      const response = await mercadoPago.getIdentificationTypes()
      setIdentificationTypes(response)
    } catch (error) {
      console.error("Erro ao buscar tipos de documento:", error)
      setErro("Não foi possível carregar os tipos de documento.")
    }
  }

  const buscarBandeiraEParcelas = async (mercadoPago, bin) => {
    try {
      if (!mercadoPago) {
        console.error("Erro: SDK MercadoPago não foi inicializado.")
        return
      }

      // Obtendo o método de pagamento com base no BIN
      const { results } = await mercadoPago.getPaymentMethods({ bin })

      if (!results || results.length === 0) {
        console.error("Nenhum método de pagamento encontrado para esse BIN.")
        return
      }

      const metodoPagamento = results[0]
      setPaymentMethodId(metodoPagamento.id)

      // Validando e formatando o valor total do pedido corretamente
      let amount = Number.parseFloat(total)
      if (isNaN(amount) || amount <= 0) {
        console.error("Erro: O valor total do pedido é inválido.", total)
        return
      }

      // Garantindo que o `amount` tenha 2 casas decimais (Ex: 2010.00)
      amount = amount.toFixed(2)

      const installmentsResponse = await mercadoPago.getInstallments({
        amount: amount,
        bin: bin,
        paymentMethodId: metodoPagamento.id,
      })

      if (!installmentsResponse || installmentsResponse.length === 0) {
        console.error("Nenhuma opção de parcelamento encontrada.")
        return
      }

      setInstallmentsOptions(installmentsResponse[0].payer_costs || [])
    } catch (error) {
      console.error("Erro ao obter métodos de pagamento e parcelas:", error)
      setErro("Erro ao obter opções de parcelamento.")
    }
  }

  const gerarCardToken = async () => {
    try {
      if (!mp) {
        console.error("Erro: SDK MercadoPago não foi inicializado.")
        return null
      }

      const token = await mp.fields.createCardToken({
        cardholderName: document.getElementById("form-checkout__cardholderName").value,
        identificationType: document.getElementById("form-checkout__identificationType").value,
        identificationNumber: document.getElementById("form-checkout__identificationNumber").value,
      })

      if (!token || !token.id) {
        console.error("Erro ao gerar token do cartão: Token inválido ou não gerado corretamente.")
        return null
      }

      setCardToken(token.id)
      return token.id
    } catch (error) {
      console.error("Erro ao gerar token do cartão:", error)
      setErro("Erro ao processar dados do cartão. Verifique as informações e tente novamente.")
      return null
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setErro("")

    try {
      const tokenId = await gerarCardToken()
      if (!tokenId) {
        throw new Error("Erro ao gerar token do cartão")
      }

      const nomeCompleto = document.getElementById("form-checkout__cardholderName").value.trim()
      const partesNome = nomeCompleto.split(/\s+/)
      const primeiroNome = partesNome[0] || "Cliente"
      const ultimoNome = partesNome.slice(1).join(" ") || "Sobrenome"

      const response = await axios.post("https://api.ticketdovaqueiro.com/api/pagamento/cartao", {
        pedidoId,
        totalPedido: Number.parseFloat(total).toFixed(2),
        token: tokenId,
        paymentMethodId: paymentMethodId,
        installments: Number(document.getElementById("form-checkout__installments").value),
        payer: {
          email: document.getElementById("form-checkout__cardholderEmail").value,
          first_name: primeiroNome,
          last_name: ultimoNome,
          identification: {
            type: document.getElementById("form-checkout__identificationType").value,
            number: document.getElementById("form-checkout__identificationNumber").value,
          },
        },
        items: [
          {
            id: pedidoId.toString(),
            title: "Senha",
            quantity: 1,
            unit_price: Number(total),
            category_id: "event_ticket", // ✅ obrigatório para antifraude
            description: `Compra de senha para vaquejada`, // ✅ descrição clara
          }
        ]
      })

      if (response.data.status === "approved") {
        setStatusPagamento("Pagamento confirmado! ✅")
        if (onSuccess) onSuccess()
      } else {
        setStatusPagamento("Pagamento pendente...")
      }
    } catch (error) {
      console.error("Erro ao processar pagamento:", error)
      setErro("Erro ao processar pagamento. Verifique os dados e tente novamente.")
      setStatusPagamento("Erro ao processar pagamento.")
    } finally {
      setLoading(false)
    }
  }

  if (sdkLoading) {
    return (
      <div className="cc-loading">
        <div className="cc-loading-spinner"></div>
        <p>Carregando formulário de pagamento...</p>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="cc-container"
    >
      <h2 className="cc-title">Pagamento com Cartão</h2>

      {erro && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="cc-erro">
          <AlertCircle size={18} />
          {erro}
        </motion.div>
      )}

      <form id="form-checkout" className="cc-form" onSubmit={handleSubmit}>
        <div id="form-checkout__cardNumber" className="cc-form-mp-container"></div>
        <div id="form-checkout__expirationDate" className="cc-form-mp-container"></div>
        <div id="form-checkout__securityCode" className="cc-form-mp-container"></div>

        <label htmlFor="form-checkout__cardholderName" className="cc-label">
          Nome no cartão
        </label>
        <input
          type="text"
          id="form-checkout__cardholderName"
          className="cc-input"
          placeholder="Nome impresso no cartão"
          required
        />

        <label htmlFor="form-checkout__identificationType" className="cc-label">
          Tipo de documento
        </label>
        <select id="form-checkout__identificationType" className="cc-select" required>
          <option value="">Selecione o tipo de documento</option>
          {identificationTypes.map((tipo) => (
            <option key={tipo.id} value={tipo.id}>
              {tipo.name}
            </option>
          ))}
        </select>

        <label htmlFor="form-checkout__identificationNumber" className="cc-label">
          Número do documento
        </label>
        <input type="text" id="form-checkout__identificationNumber" className="cc-input" placeholder="CPF" required />

        <label htmlFor="form-checkout__cardholderEmail" className="cc-label">
          E-mail
        </label>
        <input
          type="email"
          id="form-checkout__cardholderEmail"
          className="cc-input"
          placeholder="Seu e-mail"
          required
        />

        <div className="cc-form-group">
          <label className="cc-label">Bandeira do Cartão:</label>
          <p className="cc-payment-method">{paymentMethodId ? paymentMethodId : "Aguardando..."}</p>
        </div>

        <label htmlFor="form-checkout__installments" className="cc-label">
          Parcelas:
        </label>
        <select id="form-checkout__installments" className="cc-select">
          {installmentsOptions.length > 0 ? (
            installmentsOptions.map((installment, index) => (
              <option key={index} value={installment.installments}>
                {installment.recommended_message}
              </option>
            ))
          ) : (
            <option value="1">1x (à vista)</option>
          )}
        </select>

        <motion.button
          type="submit"
          className="cc-button"
          disabled={loading}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          {loading ? (
            <>
              <div className="cc-button-loader"></div>
              Processando...
            </>
          ) : (
            <>
              <CreditCard size={18} />
              Pagar Agora
            </>
          )}
        </motion.button>
      </form>

      {statusPagamento && statusPagamento !== "Aguardando pagamento..." && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`cc-status-pagamento ${
            statusPagamento.includes("confirmado") ? "cc-status-sucesso" : "cc-status-pendente"
          }`}
        >
          {statusPagamento.includes("confirmado") ? <CheckCircle size={18} /> : null}
          {statusPagamento}
        </motion.p>
      )}
    </motion.div>
  )
}

export default CheckoutCartao
